<script lang="ts" setup>
import { ref, onMounted, onUnmounted, useSlots, nextTick, provide } from 'vue';
import { useWindowScroll, watchThrottled } from '@vueuse/core';
import scrollama from 'scrollama';

const offset = 72;

const contentEl = ref();
const pandaEl = ref();
const navigatorEl = ref<HTMLElement>();
const fixed = ref(false);

const scroller = scrollama();
const slots = useSlots();

const tabLabels = ref(slots.default?.().map(tab => tab.props?.label).filter(Boolean));

const props = defineProps({
  barClass: {
    type: String,
    default: '',
  },
});

const goto = (index: number) => {
  if (!contentEl.value.children[index]) {
    return;
  }

  nextTick(() => {
    window.scrollTo({
      top: window.scrollY + contentEl.value.children[index].getBoundingClientRect().top - 120,
      left: 0,
      behavior: 'smooth',
    });
  });
}

const { y } = useWindowScroll();

onMounted(() => {
  const offsetTop = navigatorEl.value?.offsetTop ?? 0;
  const maxSteps = Array.from(contentEl.value.children).length - 1;

  watchThrottled(y, (value) => {
    fixed.value = value > offsetTop - offset;
  }, {
    throttle: 10,
  });

  scroller.setup({
    step: Array.from(contentEl.value.children),
    offset: 0.1,
    progress: true,
    // debug: true,
  }).onStepProgress(({ index, progress }) => {
    const stepNums = index === maxSteps ? index * 100 : index * 100 + (progress * 100)
    pandaEl.value.style.transform = `translateY(${stepNums}%)`;
  })
});

onUnmounted(() => {
  scroller.destroy();
});

// 组件异步加载高度不确定时调用 resizeComponent 重置组件的高度或宽度
// 需要一个短暂延时才能正确处理
const resizeComponent = () => {
  setTimeout(() => {
    scroller.resize();
  }, 100);
}

provide('floatNavigator', { resizeComponent });

defineExpose({
  goto,
});
</script>

<template>
  <div class="float-navigator">
    <div class="float-navigator__bar left-38px top-1080px z-60 h-334px flex bg-transparent <2xl:left-24px" :class="[ fixed ? 'fixed top-72px' : 'absolute', props.barClass ]" ref="navigatorEl">
      <div class="float-navigator__panda absolute left--6px top-30px mt-2px h-40px w-24px bg-contain bg-no-repeat transition-transform ease-linear" ref="pandaEl"></div>
      <div class="float-navigator__line h-full w-2px"></div>
      <ul class="m-0 ml-24px mt-30px flex flex-col list-none p-0">
        <li v-for="(item, index) in tabLabels" :key="index" class="cursor-pointer text-16px color-hex-00479d leading-32px siblings:mt-8px" @click="goto(index)">
          {{ item }}
        </li>
      </ul>
    </div>

    <div class="float-navigator__content" ref="contentEl">
      <slot></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.float-navigator {
  .float-navigator__bar {
    .float-navigator__panda {
      background-image: url(@@summit/2023/assets/common/panda.png);
    }

    .float-navigator__line {
      background: linear-gradient(180deg, rgba(0, 71, 157, 0), rgba(0, 71, 157, 1) 26.887%, rgba(0, 71, 157, 1) 49.721%, rgba(0, 71, 157, 1) 76.671%, rgba(0, 71, 157, 0) 100%);
    }
  }

  @media (max-width: 1450px) {
    .float-navigator__bar {
      display: none;
    }
  }
}
</style>
